import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays, isAfter } from "date-fns";

export type PaymentStatus = "upToDate" | "expired" | "noPayments";

export interface UserPaymentInfo {
  userId: string;
  status: PaymentStatus;
}

export const useUserPayments = (userIds: string[]) => {
  // Stabilize the cache key so it doesn't change on every render due to a new array reference
  const stableKey = [...userIds].sort().join(",");

  return useQuery({
    queryKey: ["user-payments", stableKey],
    queryFn: async (): Promise<Map<string, PaymentStatus>> => {
      if (userIds.length === 0) {
        return new Map();
      }

      // Fetch all payments in chunks + pagination to bypass 1000-row limit
      // Exclude article sales: they are not membership payments and would skew status
      const allPayments: { user_id: string; paid_at: string; validity_days: number }[] = [];
      const chunkSize = 200;
      const pageSize = 1000;

      for (let i = 0; i < userIds.length; i += chunkSize) {
        const chunk = userIds.slice(i, i + chunkSize);
        let from = 0;

        while (true) {
          const { data, error } = await supabase
            .from("payments")
            .select("user_id, paid_at, validity_days")
            .in("user_id", chunk)
            .or("is_article_sale.is.null,is_article_sale.eq.false")
            .order("paid_at", { ascending: false })
            .range(from, from + pageSize - 1);

          if (error) {
            console.error("Error fetching payments:", error);
            throw error;
          }

          if (!data || data.length === 0) break;
          allPayments.push(...data);
          if (data.length < pageSize) break;
          from += pageSize;
        }
      }

      const paymentStatusMap = new Map<string, PaymentStatus>();

      // Initialize all users as "noPayments"
      userIds.forEach((userId) => {
        paymentStatusMap.set(userId, "noPayments");
      });

      // Process: only the most recent payment per user
      const processedUsers = new Set<string>();

      allPayments.forEach((payment) => {
        if (!processedUsers.has(payment.user_id)) {
          processedUsers.add(payment.user_id);

          const expirationDate = addDays(
            new Date(payment.paid_at),
            payment.validity_days
          );
          const isUpToDate = isAfter(expirationDate, new Date());

          paymentStatusMap.set(
            payment.user_id,
            isUpToDate ? "upToDate" : "expired"
          );
        }
      });

      return paymentStatusMap;
    },
    enabled: userIds.length > 0,
  });
};
